import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Timestamp } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { Button, Input, ZebraTable } from '@kawai/ui';
import {
  FUEL_TYPES,
  KAKEURI_CATEGORY_ID,
  deriveCashFreePrice,
  deriveCashlessPrice,
  deriveKakeuriBankPrice,
  deriveKakeuriDirectDebitPrice,
  deriveLinePrice,
  getFuelPriceListRows,
  normalizeFuelSettings,
  type FuelCategory,
  type FuelDiscount,
  type FuelPricingSettings,
  type FuelType,
} from '@kawai/shared';
import { useAuth } from '../lib/auth.js';
import { useFuelPrices } from '../lib/hooks/useFuelPrices.js';
import { useFuelPriceList } from '../lib/hooks/useFuelPriceList.js';
import { useFuelPriceHistory } from '../lib/hooks/useFuelPriceHistory.js';
import { useFuelSettings } from '../lib/hooks/useFuelSettings.js';
import {
  BASE_MAX,
  BASE_MIN,
  LIST_MAX,
  LIST_MIN,
  validateBasePrice,
  validateListPrice,
} from '../lib/fuelPriceValidation.js';
import { callFuelSetPrices, callFuelSetPriceList } from '../lib/api/fuelPriceActions.js';
import type { FuelPricesDoc } from '../lib/repositories/fuelPrices.js';
import { ROUTES } from '../routes.js';

const FUEL_LABELS: Readonly<Record<FuelType, string>> = {
  haioku: 'ハイオク',
  regular: 'レギュラー',
  keiyu: '軽油',
  touyu: '灯油',
};

type BaseInputs = Record<FuelType, string>;
/** 区分 id → 4 燃料の入力文字列。区分は fuel_settings 由来のため動的（F-16）。 */
type ListInputs = Record<string, Record<FuelType, string>>;
type ListErrors = Record<string, Record<FuelType, string | undefined>>;

function emptyBaseInputs(): BaseInputs {
  return { haioku: '', regular: '', keiyu: '', touyu: '' };
}

function docToInputs(doc: FuelPricesDoc | null): BaseInputs {
  if (!doc) return emptyBaseInputs();
  return {
    haioku: String(doc.haioku ?? ''),
    regular: String(doc.regular ?? ''),
    keiyu: String(doc.keiyu ?? ''),
    touyu: String(doc.touyu ?? ''),
  };
}

/** 一覧表 doc（新旧 shape）→ 入力文字列。区分に無い row も保持し、表示側で絞る。 */
function listDocToInputs(doc: unknown): ListInputs {
  const rows = getFuelPriceListRows(doc);
  const out: ListInputs = {};
  for (const [id, row] of Object.entries(rows)) {
    out[id] = {
      haioku: row.haioku !== undefined ? String(row.haioku) : '',
      regular: row.regular !== undefined ? String(row.regular) : '',
      keiyu: row.keiyu !== undefined ? String(row.keiyu) : '',
      touyu: row.touyu !== undefined ? String(row.touyu) : '',
    };
  }
  return out;
}

function parseIntOrNaN(s: string): number {
  if (s.trim() === '') return Number.NaN;
  const n = Number(s);
  return Number.isInteger(n) ? n : Number.NaN;
}

function formatYen(n: number): string {
  return Number.isFinite(n) ? `${n} 円` : '―';
}

/** 派生行ラベルの値引き表記。共通モードは「（-N 円）」、燃料別は「（燃料別）」。 */
function discountSuffix(d: FuelDiscount): string {
  return d.common ? `（-${d.commonValue} 円）` : '（燃料別）';
}

function formatTimestamp(ts: Timestamp | undefined | null): string {
  if (!ts) return '―';
  if (typeof (ts as { toDate?: () => Date }).toDate === 'function') {
    const d = (ts as { toDate: () => Date }).toDate();
    return d.toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' });
  }
  return '―';
}

/**
 * F-02: 燃料油価格管理ページ。
 *
 * 構成:
 *  - 上段: 基準価格 (haioku / regular / keiyu / touyu) の input + 「保存」ボタン (F-03 で書込)
 *  - 中段: 派生価格プレビュー (現金フリー / キャッシュレス / LINE 価格)
 *  - 下段: 一覧表 (設定された区分 × 4 燃料 + 掛売派生)
 *  - 末尾: 直近の変更履歴 (`fuel_price_history` の最新 10 件)
 *
 * F-16: 一覧表の区分と派生価格の値引き幅は fuel_settings（設定ページ）に連動する。
 * 設定 doc が無いテナントはデフォルト（従来ハードコード互換）で動作する。
 *
 * S-136: 見た目は docs/UI-GUIDE.md の v2。各段は白カード（30/26）、表は縦横線なし
 * （見出し行は `bg-surface-2`）、入力の面は灰。入力を持つ表は行をゼブラにしない
 * （灰面の上に灰の入力 = 同色面になる）。
 */

const CARD = 'flex flex-col gap-4 rounded-xl bg-white px-[30px] py-[26px]';
const CARD_TITLE = 'text-h-card font-semibold text-ink';
const NOTE = 'text-label text-ink-3';
const ALERT = 'rounded-lg bg-surface-2 px-[18px] py-3 text-label text-danger-ink';
const ERR = 'text-label text-danger-ink';
const OK = 'text-label text-success-ink';

export function FuelPricesPage() {
  const { tenantId } = useAuth();
  const queryClient = useQueryClient();

  const fuelPricesQ = useFuelPrices(tenantId);
  const fuelPriceListQ = useFuelPriceList(tenantId);
  const fuelPriceHistoryQ = useFuelPriceHistory(tenantId, 10);
  const fuelSettingsQ = useFuelSettings(tenantId);

  const settings = useMemo(
    () => normalizeFuelSettings(fuelSettingsQ.data),
    [fuelSettingsQ.data],
  );
  const categories = settings.categories;
  const pricing = settings.pricing;

  const [baseInputs, setBaseInputs] = useState<BaseInputs>(emptyBaseInputs());
  const [hasUserEdited, setHasUserEdited] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const [listInputs, setListInputs] = useState<ListInputs>({});
  const [hasUserEditedList, setHasUserEditedList] = useState(false);
  const [submittingList, setSubmittingList] = useState(false);
  const [submitErrorList, setSubmitErrorList] = useState<string | null>(null);
  const [submitSuccessList, setSubmitSuccessList] = useState(false);

  // 初回 / 再取得時に Firestore の値で input を初期化（ユーザー編集中は上書きしない）
  useEffect(() => {
    if (fuelPricesQ.data && !hasUserEdited) {
      setBaseInputs(docToInputs(fuelPricesQ.data));
    }
  }, [fuelPricesQ.data, hasUserEdited]);

  useEffect(() => {
    if (fuelPriceListQ.data && !hasUserEditedList) {
      setListInputs(listDocToInputs(fuelPriceListQ.data));
    }
  }, [fuelPriceListQ.data, hasUserEditedList]);

  const baseErrors = useMemo<Record<FuelType, string | undefined>>(() => {
    const out = {} as Record<FuelType, string | undefined>;
    for (const f of FUEL_TYPES) {
      const n = parseIntOrNaN(baseInputs[f]);
      const r = validateBasePrice(n);
      out[f] = r.valid ? undefined : r.error;
    }
    return out;
  }, [baseInputs]);

  const hasAnyError = FUEL_TYPES.some((f) => baseErrors[f] !== undefined);

  const listErrors = useMemo<ListErrors>(() => {
    const out: ListErrors = {};
    for (const cat of categories) {
      const row = {} as Record<FuelType, string | undefined>;
      for (const f of FUEL_TYPES) {
        const n = parseIntOrNaN(listInputs[cat.id]?.[f] ?? '');
        const r = validateListPrice(n);
        row[f] = r.valid ? undefined : r.error;
      }
      out[cat.id] = row;
    }
    return out;
  }, [categories, listInputs]);

  const hasAnyListError = categories.some((cat) =>
    FUEL_TYPES.some((f) => listErrors[cat.id]?.[f] !== undefined),
  );

  async function handleSubmitBase() {
    if (hasAnyError) return;
    setSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);
    try {
      const payload = {
        haioku: parseIntOrNaN(baseInputs.haioku),
        regular: parseIntOrNaN(baseInputs.regular),
        keiyu: parseIntOrNaN(baseInputs.keiyu),
        touyu: parseIntOrNaN(baseInputs.touyu),
      };
      await callFuelSetPrices(payload);
      setSubmitSuccess(true);
      // F-03 (PR #74 review MEDIUM #1): 保存成功後に hasUserEdited をリセットして
      // 次回 fetch 時の useEffect 初期化を再有効化する。
      // F-16 レビュー #6 + 2巡目 #2: リセットは refetch **成功**時のみ。invalidateQueries は
      // 既定では refetch 失敗を握り潰して resolve するため throwOnError で判定する。
      // 失敗時は input が編集値（= 保存済みの値）を保持し、stale cache への巻き戻りを防ぐ。
      try {
        await queryClient.invalidateQueries(
          { queryKey: ['fuelPrices'] },
          { throwOnError: true },
        );
        setHasUserEdited(false);
      } catch {
        // 保存自体は成功。再取得失敗は次回の再取得で解消する
      }
      await queryClient.invalidateQueries({ queryKey: ['fuelPriceHistory'] });
    } catch (err) {
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : '保存に失敗しました';
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSubmitList() {
    if (hasAnyListError) return;
    setSubmittingList(true);
    setSubmitErrorList(null);
    setSubmitSuccessList(false);
    try {
      const rows: Record<string, Record<FuelType, number>> = {};
      for (const cat of categories) {
        rows[cat.id] = {
          haioku: parseIntOrNaN(listInputs[cat.id]?.haioku ?? ''),
          regular: parseIntOrNaN(listInputs[cat.id]?.regular ?? ''),
          keiyu: parseIntOrNaN(listInputs[cat.id]?.keiyu ?? ''),
          touyu: parseIntOrNaN(listInputs[cat.id]?.touyu ?? ''),
        };
      }
      await callFuelSetPriceList({ rows });
      setSubmitSuccessList(true);
      // F-16 レビュー #6 + 2巡目 #2: リセットは refetch 成功時のみ（基準価格側と同じ理由）
      try {
        await queryClient.invalidateQueries(
          { queryKey: ['fuelPriceList'] },
          { throwOnError: true },
        );
        setHasUserEditedList(false);
      } catch {
        // 保存自体は成功。再取得失敗は次回の再取得で解消する
      }
      await queryClient.invalidateQueries({ queryKey: ['fuelPriceHistory'] });
    } catch (err) {
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : '保存に失敗しました';
      setSubmitErrorList(msg);
    } finally {
      setSubmittingList(false);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <header>
        {/* S-10: Layout の sticky ヘッダがブランド <h1> を持つため、ページ見出しは <h2> に降格 */}
        <h2 className="text-h-page font-semibold text-ink">燃料価格</h2>
        <p className="mt-1 text-body text-ink-2">
          基準価格を変更すると、現金フリー / キャッシュレス / LINE
          価格は自動計算されます。 保存ボタンで Cloud Functions 経由で更新（履歴 +
          監査ログを同時記録）。
        </p>
      </header>

      {/* F-16 レビュー #5: 設定の読取失敗を無警告でデフォルトにフォールバックさせない */}
      {fuelSettingsQ.error && (
        <p role="alert" className={ALERT}>
          設定の取得に失敗しました（既定の区分・値引き幅で表示しています）:{' '}
          {fuelSettingsQ.error.message}
        </p>
      )}

      {/* F-11: 店頭掲示用ポスター / 価格一覧表へのリンク（別タブで開くだけ。各ページ内の「印刷」ボタンで実印刷）。
          ページ地色の上に直接置くので副リンクは白面（bg-surface-2 は地色と見分けがつかない） */}
      <section aria-label="印刷ページへのリンク" className="flex flex-wrap gap-2">
        <a
          href={ROUTES.dwPrice}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center rounded-lg bg-brand px-5 py-2 text-body font-medium text-white hover:opacity-90"
        >
          価格表を印刷（店頭掲示用）
        </a>
        <a
          href={ROUTES.priceList}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center rounded-lg bg-white px-4 py-2 text-body font-medium text-ink hover:bg-surface-3"
        >
          価格一覧表を印刷
        </a>
      </section>

      {/* ===== 上段: 基準価格 ===== */}
      <section aria-labelledby="base-section" className={CARD}>
        <h2 id="base-section" className={CARD_TITLE}>
          基準価格（円 / リットル）
        </h2>
        {fuelPricesQ.isLoading && (
          <p role="status" className={NOTE}>
            読み込み中…
          </p>
        )}
        {fuelPricesQ.error && (
          <p role="alert" className={ALERT}>
            基準価格の取得に失敗しました: {fuelPricesQ.error.message}
          </p>
        )}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {FUEL_TYPES.map((f) => {
            const inputId = `base-${f}`;
            const errorId = `base-error-${f}`;
            return (
              <div key={f} className="flex flex-col gap-1.5">
                <label htmlFor={inputId} className="text-label font-medium text-ink-2">
                  {FUEL_LABELS[f]}
                </label>
                <Input
                  id={inputId}
                  type="number"
                  inputMode="numeric"
                  min={BASE_MIN}
                  max={BASE_MAX}
                  step={1}
                  value={baseInputs[f]}
                  disabled={submitting}
                  onChange={(e) => {
                    setHasUserEdited(true);
                    setBaseInputs((prev) => ({ ...prev, [f]: e.target.value }));
                  }}
                  invalid={Boolean(baseErrors[f])}
                  aria-invalid={Boolean(baseErrors[f])}
                  aria-describedby={baseErrors[f] ? errorId : undefined}
                  placeholder="例: 175"
                  className="ui-num"
                />
                {baseErrors[f] && (
                  <p id={errorId} role="alert" className={ERR}>
                    {baseErrors[f]}
                  </p>
                )}
              </div>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="button"
            disabled={submitting || hasAnyError}
            onClick={() => {
              void handleSubmitBase();
            }}
          >
            {submitting ? '保存中…' : '保存'}
          </Button>
          {hasAnyError && <span className={ERR}>入力にエラーがあります</span>}
          {submitError && (
            <span role="alert" className={ERR}>
              {submitError}
            </span>
          )}
          {submitSuccess && !submitError && (
            <span role="status" className={OK}>
              保存しました
            </span>
          )}
          <span className={`ml-auto ${NOTE}`}>
            最終更新:{' '}
            <span className="ui-num tabular-nums">
              {formatTimestamp(fuelPricesQ.data?.updatedAt)}
            </span>
          </span>
        </div>
      </section>

      {/* ===== 中段: 派生価格プレビュー ===== */}
      <section aria-labelledby="derived-section" className={CARD}>
        <h2 id="derived-section" className={CARD_TITLE}>
          派生価格プレビュー（基準価格から自動計算 / 円）
        </h2>
        <div className="overflow-x-auto">
          <ZebraTable className="min-w-full" aria-label="派生価格プレビュー">
            <thead>
              <ZebraTable.HeadRow>
                <ZebraTable.Th edge="first">区分</ZebraTable.Th>
                {FUEL_TYPES.map((f, i) => (
                  <ZebraTable.Th
                    key={f}
                    align="right"
                    edge={i === FUEL_TYPES.length - 1 ? 'last' : undefined}
                  >
                    {FUEL_LABELS[f]}
                  </ZebraTable.Th>
                ))}
              </ZebraTable.HeadRow>
            </thead>
            <tbody>
              <DerivedRow
                label="現金フリー"
                values={FUEL_TYPES.map((f) => {
                  const n = parseIntOrNaN(baseInputs[f]);
                  return Number.isFinite(n) ? deriveCashFreePrice(n) : Number.NaN;
                })}
              />
              <DerivedRow
                label={`キャッシュレス${discountSuffix(pricing.cashless)}`}
                values={FUEL_TYPES.map((f) => {
                  const n = parseIntOrNaN(baseInputs[f]);
                  return Number.isFinite(n)
                    ? deriveCashlessPrice(n, f, pricing)
                    : Number.NaN;
                })}
              />
              <DerivedRow
                label={`LINE 会員${discountSuffix(pricing.line)}`}
                values={FUEL_TYPES.map((f) => {
                  const n = parseIntOrNaN(baseInputs[f]);
                  return Number.isFinite(n) ? deriveLinePrice(n, f, pricing) : Number.NaN;
                })}
              />
            </tbody>
          </ZebraTable>
        </div>
      </section>

      {/* ===== 下段: 一覧表 ===== */}
      <section aria-labelledby="list-section" className={CARD}>
        <div className="flex flex-wrap items-baseline gap-3">
          <h2 id="list-section" className={CARD_TITLE}>
            一覧表（個別販売単価 / 円）
          </h2>
          <Link to={ROUTES.settings} className="text-label text-ink-2 underline hover:text-ink">
            区分・値引き幅は設定で変更
          </Link>
        </div>
        {fuelPriceListQ.isLoading && (
          <p role="status" className={NOTE}>
            読み込み中…
          </p>
        )}
        {fuelPriceListQ.error && (
          <p role="alert" className={ALERT}>
            一覧表の取得に失敗しました: {fuelPriceListQ.error.message}
          </p>
        )}
        {categories.length === 0 ? (
          <p className="text-body text-ink-2">
            区分がありません。
            <Link to={ROUTES.settings} className="underline">
              設定
            </Link>
            で区分を追加すると一覧表を入力できます。
          </p>
        ) : (
          <>
            <FuelPriceListEditor
              categories={categories}
              pricing={pricing}
              inputs={listInputs}
              errors={listErrors}
              disabled={submittingList}
              onChange={(catId, fuel, value) => {
                setHasUserEditedList(true);
                setListInputs((prev) => ({
                  ...prev,
                  [catId]: {
                    haioku: prev[catId]?.haioku ?? '',
                    regular: prev[catId]?.regular ?? '',
                    keiyu: prev[catId]?.keiyu ?? '',
                    touyu: prev[catId]?.touyu ?? '',
                    [fuel]: value,
                  },
                }));
              }}
            />
            <div className="flex flex-wrap items-center gap-3">
              <Button
                type="button"
                disabled={submittingList || hasAnyListError}
                onClick={() => {
                  void handleSubmitList();
                }}
              >
                {submittingList ? '保存中…' : '一覧表を保存'}
              </Button>
              {hasAnyListError && <span className={ERR}>入力にエラーがあります</span>}
              {submitErrorList && (
                <span role="alert" className={ERR}>
                  {submitErrorList}
                </span>
              )}
              {submitSuccessList && !submitErrorList && (
                <span role="status" className={OK}>
                  保存しました
                </span>
              )}
              <span className={`ml-auto ${NOTE}`}>
                最終更新:{' '}
                <span className="ui-num tabular-nums">
                  {formatTimestamp(fuelPriceListQ.data?.updatedAt)}
                </span>
              </span>
            </div>
          </>
        )}
      </section>

      {/* ===== 末尾: 履歴 ===== */}
      <section aria-labelledby="history-section" className={CARD}>
        <h2 id="history-section" className={CARD_TITLE}>
          直近の変更履歴
        </h2>
        {fuelPriceHistoryQ.isLoading && (
          <p role="status" className={NOTE}>
            読み込み中…
          </p>
        )}
        {fuelPriceHistoryQ.error && (
          <p role="alert" className={ALERT}>
            履歴の取得に失敗しました: {fuelPriceHistoryQ.error.message}
          </p>
        )}
        {fuelPriceHistoryQ.data && fuelPriceHistoryQ.data.length === 0 && (
          <p className="text-body text-ink-2">履歴はありません。</p>
        )}
        {fuelPriceHistoryQ.data && fuelPriceHistoryQ.data.length > 0 && (
          <ul className="flex flex-col gap-2">
            {fuelPriceHistoryQ.data.map((entry) => (
              <li key={entry.id} className="rounded-lg bg-surface-2 px-[18px] py-3">
                <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                  <span className="text-label font-medium text-ink">
                    [{entry.kind}] {entry.summary ?? '(変更内容なし)'}
                  </span>
                  <span className="ui-num text-label tabular-nums text-ink-3">
                    {formatTimestamp(entry.createdAt)}
                  </span>
                </div>
                <div className="mt-0.5 text-label text-ink-3">by {entry.createdBy}</div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

interface DerivedRowProps {
  label: string;
  values: readonly number[];
}

function DerivedRow({ label, values }: DerivedRowProps) {
  return (
    <ZebraTable.Row>
      <th scope="row" className="px-3.5 py-2.5 text-left font-medium text-ink">
        {label}
      </th>
      {values.map((v, i) => (
        <ZebraTable.Td key={i} numeric>
          {formatYen(v)}
        </ZebraTable.Td>
      ))}
    </ZebraTable.Row>
  );
}

interface FuelPriceListEditorProps {
  categories: readonly FuelCategory[];
  pricing: FuelPricingSettings;
  inputs: ListInputs;
  errors: ListErrors;
  /** 一覧表保存の実行中は入力を凍結する（2巡目 #3: 保存中編集の無言破棄を防ぐ） */
  disabled: boolean;
  onChange: (catId: string, fuel: FuelType, value: string) => void;
}

function FuelPriceListEditor({
  categories,
  pricing,
  inputs,
  errors,
  disabled,
  onChange,
}: FuelPriceListEditorProps) {
  const kakeuri = categories.find((c) => c.id === KAKEURI_CATEGORY_ID);
  return (
    <div className="overflow-x-auto">
      {/* 入力を持つ行はゼブラにしない（灰面の上の灰の入力は見分けがつかない）。見出し行だけ灰面 */}
      <ZebraTable className="min-w-full" aria-label="一覧表（個別販売単価）">
        <thead>
          <ZebraTable.HeadRow>
            <ZebraTable.Th edge="first">区分</ZebraTable.Th>
            {FUEL_TYPES.map((f, i) => (
              <ZebraTable.Th key={f} edge={i === FUEL_TYPES.length - 1 ? 'last' : undefined}>
                {FUEL_LABELS[f]}
              </ZebraTable.Th>
            ))}
          </ZebraTable.HeadRow>
        </thead>
        <tbody>
          {categories.map((cat) => (
            <tr key={cat.id}>
              <th scope="row" className="px-3.5 py-2.5 text-left font-medium text-ink">
                {cat.label}
              </th>
              {FUEL_TYPES.map((f) => {
                const inputId = `list-${cat.id}-${f}`;
                const errorId = `list-error-${cat.id}-${f}`;
                const err = errors[cat.id]?.[f];
                return (
                  <ZebraTable.Td key={f} className="align-top">
                    <Input
                      id={inputId}
                      type="number"
                      inputMode="numeric"
                      min={LIST_MIN}
                      max={LIST_MAX}
                      step={1}
                      size="sm"
                      fullWidth={false}
                      value={inputs[cat.id]?.[f] ?? ''}
                      disabled={disabled}
                      onChange={(e) => {
                        onChange(cat.id, f, e.target.value);
                      }}
                      className="ui-num w-24"
                      aria-label={`${cat.label} ${FUEL_LABELS[f]}`}
                      invalid={Boolean(err)}
                      aria-invalid={Boolean(err)}
                      aria-describedby={err ? errorId : undefined}
                    />
                    {err && (
                      <p id={errorId} role="alert" className={`mt-1 ${ERR}`}>
                        {err}
                      </p>
                    )}
                  </ZebraTable.Td>
                );
              })}
            </tr>
          ))}
          {kakeuri && (
            <>
              <KakeuriDerivedRow
                label={`${kakeuri.label} 振込${discountSuffix(pricing.kakeuriBank)}`}
                kakeuriInputs={inputs[kakeuri.id]}
                derive={(n, f) => deriveKakeuriBankPrice(n, f, pricing)}
              />
              <KakeuriDerivedRow
                label={`${kakeuri.label} 口座振替${discountSuffix(pricing.kakeuriDirectDebit)}`}
                kakeuriInputs={inputs[kakeuri.id]}
                derive={(n, f) => deriveKakeuriDirectDebitPrice(n, f, pricing)}
              />
            </>
          )}
        </tbody>
      </ZebraTable>
    </div>
  );
}

interface KakeuriDerivedRowProps {
  label: string;
  kakeuriInputs: Record<FuelType, string> | undefined;
  derive: (n: number, fuel: FuelType) => number;
}

/** 掛売の派生行（表示のみ）。入力行と区別するため灰面に置く。 */
function KakeuriDerivedRow({ label, kakeuriInputs, derive }: KakeuriDerivedRowProps) {
  return (
    <tr className="bg-surface-2">
      <th scope="row" className="px-3.5 py-2.5 text-left font-medium text-ink-2">
        {label}
      </th>
      {FUEL_TYPES.map((f) => {
        const n = parseIntOrNaN(kakeuriInputs?.[f] ?? '');
        const v = Number.isFinite(n) ? derive(n, f) : Number.NaN;
        return (
          <ZebraTable.Td key={f} tone="muted" className="text-left">
            {formatYen(v)}
          </ZebraTable.Td>
        );
      })}
    </tr>
  );
}
